import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ENDPOINTS } from '@/config/endpoints';
import type { User } from '@/types/user';
import type { AppCard, AppCardAdmin } from '@/types/appCard';
import type { Ticket, TicketDetail, TicketStatus } from '@/types/helpdesk';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/';

export type Settings = {
    defaultLanguage: string;
    title: string;
    domain: string;
    baseUrl: string;
    supportEmail: string;
    supportPhone: string;
    supportUrl: string;
};

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE_URL,
        credentials: 'include', // Include cookies for authentication
        prepareHeaders: async (headers, { endpoint }) => {
            if (
                [
                    'logout',
                    'refreshLogin',
                    'createTicket',
                    'updateTicket',
                    'addTicketComment',
                    'changePicture',
                ].includes(endpoint)
            ) {
                const response = await fetch(
                    new URL(
                        `${ENDPOINTS.authenticate()}/session`,
                        new URL(API_BASE_URL, window.location.origin)
                    ),
                    { credentials: 'include', cache: 'no-store' }
                );
                if (!response.ok) throw new Error('Could not check sign-in session.');
                const session = await response.json();
                headers.set('X-CSRF-Token', session.csrf);
            }
            headers.set('Accept', '*/*');
            // FormData bodies: the browser sets the multipart Content-Type with its boundary.
            if (!['createTicket', 'changePicture'].includes(endpoint))
                headers.set('Content-Type', 'application/json');
            return headers;
        },
    }),
    tagTypes: ['User', 'AppCards', 'AdminApps', 'Settings', 'Tickets'],
    endpoints: (builder) => ({
        getGuestTicket: builder.query<TicketDetail, { id: string; token: string }>({
            query: ({ id, token }) => ({
                url: `rest/GuestHelpdesk/${id}`,
                headers: { 'X-Ticket-Token': token },
            }),
            providesTags: ['Tickets'],
        }),
        addGuestComment: builder.mutation<
            { message: string },
            { id: string; token: string; body: string }
        >({
            query: ({ id, token, body }) => ({
                url: `rest/GuestHelpdesk/${id}/comments`,
                method: 'POST',
                headers: { 'X-Ticket-Token': token },
                body: { body },
            }),
            invalidatesTags: ['Tickets'],
        }),
        getTickets: builder.query<{ tickets: Ticket[] }, boolean>({
            query: (admin) => `rest/Helpdesk${admin ? '?admin=1' : ''}`,
            providesTags: ['Tickets'],
        }),
        getTicket: builder.query<TicketDetail, string>({
            query: (id) => `rest/Helpdesk/${id}`,
            providesTags: ['Tickets'],
        }),
        createTicket: builder.mutation<{ id: string }, FormData>({
            query: (body) => ({ url: 'rest/Helpdesk', method: 'POST', body }),
            invalidatesTags: ['Tickets'],
        }),
        updateTicket: builder.mutation<
            { message: string },
            { id: string; status: TicketStatus; information: string }
        >({
            query: ({ id, ...body }) => ({ url: `rest/Helpdesk/${id}`, method: 'PUT', body }),
            invalidatesTags: ['Tickets'],
        }),
        addTicketComment: builder.mutation<{ message: string }, { id: string; body: string }>({
            query: ({ id, body }) => ({
                url: `rest/Helpdesk/${id}/comments`,
                method: 'POST',
                body: { body },
            }),
            invalidatesTags: ['Tickets'],
        }),
        // Settings endpoints
        getSettings: builder.query<Settings, void>({
            query: () => ENDPOINTS.settings(),
            providesTags: ['Settings'],
        }),

        // User endpoints
        getProfile: builder.query<User, void>({
            query: () => ENDPOINTS.profile(),
            providesTags: ['User'],
        }),

        // A `picture` file, or `remove=1`; the IdP stores it where the person signs in.
        changePicture: builder.mutation<{ picture: string | null }, FormData>({
            query: (body) => ({ url: `${ENDPOINTS.profile()}/picture`, method: 'POST', body }),
            invalidatesTags: ['User'],
        }),

        // Lightweight session check (does not redirect)
        checkSession: builder.query<{ authenticated: boolean }, void>({
            query: () => `${ENDPOINTS.authenticate()}/session`,
            providesTags: ['User'],
        }),

        // Attempt to silently restore the session server-side (no redirects)
        silentLogin: builder.query<
            { authenticated: boolean; restored?: boolean; autoLoginBlocked?: boolean },
            void
        >({
            query: () => `${ENDPOINTS.authenticate()}/silent`,
        }),

        logout: builder.mutation<{ success: string; oidc: boolean; logoutUrl?: string }, void>({
            query: () => ({
                url: `${ENDPOINTS.authenticate()}/logout`,
                method: 'POST',
            }),
            invalidatesTags: ['User', 'AppCards', 'Tickets'],
        }),

        refreshLogin: builder.mutation<{ success: string; loginUrl: string }, void>({
            query: () => ({
                url: `${ENDPOINTS.authenticate()}/refresh`,
                method: 'POST',
            }),
        }),

        // App Cards endpoints (for regular users)
        getAppCards: builder.query<{ apps: AppCard[] }, void>({
            query: () => ENDPOINTS.appCards(),
            providesTags: ['AppCards'],
        }),

        // Admin endpoints
        getAdminApps: builder.query<{ apps: AppCardAdmin[] }, void>({
            query: () => ENDPOINTS.admin(),
            providesTags: ['AdminApps'],
        }),

        createApp: builder.mutation<{ id: string; message: string }, Partial<AppCardAdmin>>({
            query: (data) => ({
                url: ENDPOINTS.admin(),
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),

        updateApp: builder.mutation<
            { message: string },
            { id: string; data: Partial<AppCardAdmin> }
        >({
            query: ({ id, data }) => ({
                url: ENDPOINTS.adminApp(id),
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),

        deleteApp: builder.mutation<{ message: string }, string>({
            query: (id) => ({
                url: ENDPOINTS.adminApp(id),
                method: 'DELETE',
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),

        reorderApps: builder.mutation<{ message: string }, { id: string; newPosition: number }>({
            query: ({ id, newPosition }) => ({
                url: ENDPOINTS.adminReorder(id, newPosition),
                method: 'PUT',
            }),
            invalidatesTags: ['AdminApps', 'AppCards'],
        }),
    }),
});

export const {
    useGetGuestTicketQuery,
    useAddGuestCommentMutation,
    useGetTicketsQuery,
    useGetTicketQuery,
    useCreateTicketMutation,
    useUpdateTicketMutation,
    useAddTicketCommentMutation,
    useGetSettingsQuery,
    useGetProfileQuery,
    useChangePictureMutation,
    useCheckSessionQuery,
    useSilentLoginQuery,
    useLazySilentLoginQuery,
    useLogoutMutation,
    useRefreshLoginMutation,
    useGetAppCardsQuery,
    useGetAdminAppsQuery,
    useCreateAppMutation,
    useUpdateAppMutation,
    useDeleteAppMutation,
    useReorderAppsMutation,
} = apiSlice;
